// shadcn/ui Data Table guide ("Reusable Components"): https://ui.shadcn.com/docs/components/radix/data-table
import React from 'react';
import { type Column, type RowData } from '@tanstack/react-table';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';

import { useTranslation } from 'src/i18n';
import { cn } from 'src/component/ds/utils';
import { Button } from 'src/component/ds/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'src/component/ds/ui/dropdownMenu';

import { type DataTableFeatures } from 'src/component/ds/ui/dataTableFeatures';

interface DataTableColumnHeaderProps<TData extends RowData, TValue>
  extends React.HTMLAttributes<HTMLDivElement> {
  column: Column<DataTableFeatures, TData, TValue>
  title: string
}

export function DataTableColumnHeader<TData extends RowData, TValue> ({
  column,
  title,
  className,
}: DataTableColumnHeaderProps<TData, TValue>) {
  // Local changes to the shadcn source: menu labels are translated, and there's
  // no Hide item (nothing in the app can show a hidden column again).
  const { t } = useTranslation();

  if (!column.getCanSort()) {
    return <div className={cn(className)}>{title}</div>;
  }

  return (
    <div className={cn('ds-flex ds-items-center ds-gap-2', className)}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant='ghost'
            size='sm'
            className='-ds-ml-3 ds-h-8 data-[state=open]:ds-bg-accent'
          >
            <span>{title}</span>
            {column.getIsSorted() === 'desc' ? (
              <ArrowDownwardIcon fontSize='inherit' aria-hidden='true' />
            ) : column.getIsSorted() === 'asc' ? (
              <ArrowUpwardIcon fontSize='inherit' aria-hidden='true' />
            ) : (
              <UnfoldMoreIcon fontSize='inherit' aria-hidden='true' />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='start'>
          <DropdownMenuItem onClick={() => column.toggleSorting(false)}>
            <ArrowUpwardIcon fontSize='inherit' aria-hidden='true' />
            {t('table.sortAscending')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => column.toggleSorting(true)}>
            <ArrowDownwardIcon fontSize='inherit' aria-hidden='true' />
            {t('table.sortDescending')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
